import React, { useState } from "react";
import { API_BASE_URL } from "../config";
import { getToken } from "../lib/tokenStorage";

interface ProductImage {
  id?: number;
  src: string;
  alt?: string;
  name?: string;
}

interface ProductImageManagerProps {
  mainImage: ProductImage | null;
  galleryImages: ProductImage[];
  onMainImageChange: (image: ProductImage | null) => void;
  onGalleryImagesChange: (images: ProductImage[]) => void;
}

export default function ProductImageManager({
  mainImage,
  galleryImages,
  onMainImageChange,
  onGalleryImagesChange,
}: ProductImageManagerProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const uploadImage = async (file: File): Promise<string | null> => {
    try {
      const formData = new FormData();
      formData.append("upload", file);

      const token = getToken();
      if (!token) {
        throw new Error("Not authenticated. Please log in first.");
      }

      const response = await fetch(`${API_BASE_URL}/upload.php`, {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Upload failed");
      }

      const data = await response.json();
      if (data.success && data.url) {
        return data.url;
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (error: any) {
      setUploadError(error.message);
      return null;
    }
  };

  const handleMainImageUpload = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);
      setUploadError(null);
      const url = await uploadImage(file);
      if (url) {
        onMainImageChange({
          src: url,
          alt: file.name,
          name: file.name,
        });
      }
    } finally {
      setIsUploading(false);
      // Reset the input so the same file can be uploaded again
      e.target.value = "";
    }
  };

  const handleGalleryUpload = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = e.target.files;
    if (!files) return;

    try {
      setIsUploading(true);
      setUploadError(null);

      const newImages: ProductImage[] = [];
      for (let i = 0; i < files.length; i++) {
        const url = await uploadImage(files[i]);
        if (url) {
          newImages.push({
            src: url,
            alt: files[i].name,
            name: files[i].name,
          });
        }
      }

      if (newImages.length > 0) {
        onGalleryImagesChange([...galleryImages, ...newImages]);
      }
    } finally {
      setIsUploading(false);
      // Reset the input so the same file can be uploaded again
      e.target.value = "";
    }
  };

  const removeMainImage = () => {
    onMainImageChange(null);
  };

  const removeGalleryImage = (index: number) => {
    onGalleryImagesChange(galleryImages.filter((_, i) => i !== index));
  };

  const setMainImageFromGallery = (index: number) => {
    const image = galleryImages[index];
    onMainImageChange(image);
    removeGalleryImage(index);
  };

  return (
    <div className="card mb-3">
      <div className="card-body">
        <h6 className="card-title mb-3">
          <i className="fa fa-image me-2"></i>Product Images
        </h6>

        {uploadError && (
          <div className="alert alert-danger mb-3">{uploadError}</div>
        )}

        {/* Main Image Section */}
        <div className="mb-4">
          <label className="form-label fw-semibold">Main Product Image</label>
          <div className="border rounded p-3 bg-light">
            {mainImage ? (
              <div className="position-relative d-inline-block">
                <img
                  src={mainImage.src}
                  alt={mainImage.alt || "Product"}
                  style={{
                    maxWidth: "200px",
                    maxHeight: "200px",
                    objectFit: "cover",
                    borderRadius: "4px",
                  }}
                />
                <button
                  type="button"
                  className="btn btn-sm btn-danger position-absolute top-0 end-0 m-2"
                  onClick={removeMainImage}
                  title="Remove main image"
                  style={{ padding: "4px 8px" }}
                >
                  <i className="fa fa-trash"></i>
                </button>
              </div>
            ) : (
              <div className="text-center py-4 text-muted">
                <i
                  className="fa fa-image"
                  style={{ fontSize: "48px", opacity: 0.3 }}
                ></i>
                <p className="mb-0 mt-2">No main image selected</p>
              </div>
            )}
            <div className="mt-3">
              <input
                type="file"
                id="mainImageInput"
                accept="image/*"
                onChange={handleMainImageUpload}
                disabled={isUploading}
                style={{ display: "none" }}
              />
              <label
                htmlFor="mainImageInput"
                className="btn btn-outline-primary btn-sm"
              >
                <i className="fa fa-upload me-1"></i>
                {isUploading ? "Uploading…" : "Upload Image"}
              </label>
            </div>
          </div>
        </div>

        {/* Gallery Images Section */}
        <div>
          <label className="form-label fw-semibold">Gallery Images</label>
          <div className="border rounded p-3 bg-light">
            {galleryImages.length > 0 ? (
              <div className="row g-2">
                {galleryImages.map((image, index) => (
                  <div key={index} className="col-auto">
                    <div className="position-relative">
                      <img
                        src={image.src}
                        alt={image.alt || `Gallery ${index}`}
                        style={{
                          width: "120px",
                          height: "120px",
                          objectFit: "cover",
                          borderRadius: "4px",
                          border: "1px solid #dee2e6",
                        }}
                      />
                      <div
                        className="position-absolute top-0 start-0 w-100 h-100 d-flex gap-1 p-1 bg-dark bg-opacity-75 rounded"
                        style={{ opacity: 1 }}
                      >
                        <button
                          type="button"
                          className="btn btn-sm btn-warning flex-grow-1"
                          onClick={() => setMainImageFromGallery(index)}
                          title="Set as main image"
                          style={{ width: 24, height: 24, padding: 0 }}
                        >
                          <i className="fa fa-star"></i>
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-danger flex-grow-1"
                          onClick={() => removeGalleryImage(index)}
                          title="Remove from gallery"
                          style={{ width: 24, height: 24, padding: 0 }}
                        >
                          <i className="fa fa-trash"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4 text-muted">
                <i
                  className="fa fa-images"
                  style={{ fontSize: "48px", opacity: 0.3 }}
                ></i>
                <p className="mb-0 mt-2">No gallery images uploaded</p>
              </div>
            )}
            <div className="mt-3">
              <input
                type="file"
                id="galleryInput"
                accept="image/*"
                multiple
                onChange={handleGalleryUpload}
                disabled={isUploading}
                style={{ display: "none" }}
              />
              <label
                htmlFor="galleryInput"
                className="btn btn-outline-primary btn-sm"
              >
                <i className="fa fa-upload me-1"></i>
                {isUploading ? "Uploading…" : "Add Gallery Images"}
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
